import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import type { Feature } from '../../schemas/feature.js'
import type { UnitSystem } from '../../utils/units.js'
import type { ActivityType } from 'cairn-compass'
import type { MapMode, DrawSubContext } from '../../services/trip-feature-manager.js'

import './status-bar.js'
import './timeline-bar.js'
import './drawing-tools-bar.js'
import './editing-tools-bar.js'
import './selection-tools-bar.js'
import './measure-tools-bar.js'
import './elevation-profile.js'
import './distance-time-chart.js'
import './track-aspect-rose.js'
import type { TimelineState } from '../../services/timeline/timeline-service.js'
import type { ProfilePoint, ProfileWaypoint } from './elevation-profile.js'
import type { DistanceTimePoint } from './distance-time-chart.js'
import type { AspectCounts } from './track-aspect-rose.js'

type ExpandedTab = 'elevation' | 'distance' | 'aspect'

const COLLAPSED_HEIGHT = 60
const EXPANDED_HEIGHT = 400
const DRAG_THRESHOLD = 15 // px of drag before we commit to expand/collapse

@customElement('cairn-context-bar')
export class CairnContextBar extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      color: white;
      padding: 0;
      box-sizing: border-box;
      height: var(--bar-height, 60px);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      transition: height 0.25s ease;
      overflow: hidden;
    }

    :host([dragging]) {
      transition: none;
    }

    .collapsed-bar {
      position: relative;
      width: 100%;
      height: 60px;
      flex-shrink: 0;
    }

    .context-scroll {
      position: relative;
      width: 100%;
      height: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE/Edge */
    }

    .context-scroll::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }

    .context-content {
      display: flex;
      align-items: stretch;
      min-width: 100%;
      width: max-content;
      height: 100%;
    }

    /* Let child bars take their natural width instead of clamping to 100% */
    .context-content > * {
      flex-shrink: 0;
    }

    /* Overflow shadow indicators — on :host so they don't scroll */
    .overflow-left,
    .overflow-right {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 24px;
      pointer-events: none;
      z-index: 1;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    .overflow-left {
      left: 0;
      background: linear-gradient(to right, rgba(0, 0, 0, 0.5), transparent);
    }

    .overflow-right {
      right: 0;
      background: linear-gradient(to left, rgba(0, 0, 0, 0.5), transparent);
    }

    :host([overflow-left]) .overflow-left {
      opacity: 1;
    }

    :host([overflow-right]) .overflow-right {
      opacity: 1;
    }

    :host([hidden]) {
      display: none;
    }

    /* Drag handle — overlaps bottom of collapsed bar */
    .drag-handle {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 20px;
      cursor: grab;
      touch-action: none;
      z-index: 2;
    }

    .drag-handle-pill {
      width: 36px;
      height: 4px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.4);
    }

    .drag-handle:active {
      cursor: grabbing;
    }

    /* Expanded content area — height = total - collapsed bar - drag handle - padding */
    .expanded-content {
      height: calc(var(--bar-height, 400px) - 60px - 20px - 8px);
      overflow: hidden;
      padding: 0 12px 8px;
      display: flex;
      flex-direction: column;
    }

    .tab-bar {
      display: flex;
      gap: 4px;
      margin-bottom: 6px;
      flex-shrink: 0;
    }

    .tab-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      padding: 3px 10px;
      cursor: pointer;
    }

    .tab-btn[active] {
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border-color: rgba(255, 255, 255, 0.4);
    }

    .tab-content {
      flex: 1;
      min-height: 0;
    }
  `

  @property({ type: String })
  mapMode: MapMode = 'map'

  @property({ type: String })
  drawSubContext: DrawSubContext = 'tools'

  @property({ type: Object })
  currentLocation?: { lat: number; lng: number; accuracy?: number }

  @property({ type: Number })
  currentElevation?: number

  @property({ type: Number })
  currentHeading?: number

  @property({ type: Number })
  currentVelocity?: number

  @property({ type: String })
  unitSystem: UnitSystem = 'imperial'

  @property({ type: Boolean })
  locationPermissionGranted = false

  @property({ type: Boolean })
  isRecording = false

  @property({ type: String })
  currentActivity?: ActivityType

  @property({ type: Array })
  selectedFeatures: Feature[] = []

  @property({ type: Number }) userFeatureCount = 0
  @property({ type: Number }) overlayFeatureCount = 0
  @property({ type: Boolean }) hasPlanContext = false

  @property({ type: Boolean })
  splitEnabled = false

  @property({ type: Boolean })
  joinPreview = false

  @property({ type: String })
  selectedTool?: string

  @property({ type: String })
  activeStyleProperty?: string

  @property({ type: String })
  measureDistance?: string

  @property({ type: String })
  measureBearing?: string

  @property({ type: String })
  measureArea?: string

  @property({ type: String })
  measurePerimeter?: string

  @property({ type: String })
  measureType: 'none' | 'line' | 'shape' | 'point' = 'none'

  @property({ type: String })
  measureLocation?: string

  @property({ type: Object })
  timelineState?: TimelineState

  /** Route elevation profile for expanded chart */
  @property({ type: Array })
  routeProfile: ProfilePoint[] = []

  /** Recorded track elevation overlay */
  @property({ type: Array })
  trackProfile: ProfilePoint[] = []

  /** Current position distance along route (meters) */
  @property({ type: Number })
  currentRouteDistance?: number

  /** Waypoints along the route */
  @property({ type: Array })
  routeWaypoints: ProfileWaypoint[] = []

  /** Distance vs time data */
  @property({ type: Array })
  distanceTime: DistanceTimePoint[] = []

  /** Aspect distribution counts */
  @property({ type: Array })
  aspectCounts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]

  // Legacy compat
  @property({ type: String })
  mode: 'location' | 'drawing' | 'editing' | 'extending' = 'location'

  @state()
  private expanded = false

  @state()
  private activeTab: ExpandedTab = 'elevation'

  /** Whether expanded content is available for the current mode */
  private get expandable(): boolean {
    return this.mapMode === 'tracking'
  }

  // Drag-to-scroll state (horizontal)
  private isHScrollDragging = false
  private dragStartX = 0
  private scrollStartX = 0
  private hasDragMoved = false
  private resizeObserver?: ResizeObserver

  // Drag-to-expand state (vertical handle)
  private isExpandDragging = false
  private expandDragStartY = 0
  private expandStartHeight = COLLAPSED_HEIGHT

  private get scrollEl(): HTMLElement | null {
    return this.shadowRoot?.querySelector('.context-scroll') ?? null
  }

  override firstUpdated(): void {
    this.setupDragScroll()
    this.checkOverflow()

    // Re-check overflow when the bar resizes
    this.resizeObserver = new ResizeObserver(() => this.checkOverflow())
    const el = this.scrollEl
    if (el) this.resizeObserver.observe(el)
  }

  override updated(): void {
    // Content may have changed — re-check overflow after render
    requestAnimationFrame(() => this.checkOverflow())

    // Collapse if we switch to a non-expandable mode
    if (!this.expandable && this.expanded) {
      this.expanded = false
      this.style.setProperty('--bar-height', `${COLLAPSED_HEIGHT}px`)
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    this.cleanupExpandDrag()
  }

  private checkOverflow(): void {
    const el = this.scrollEl
    if (!el) return
    const hasLeft = el.scrollLeft > 1
    const hasRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    this.toggleAttribute('overflow-left', hasLeft)
    this.toggleAttribute('overflow-right', hasRight)
  }

  private setupDragScroll(): void {
    const el = this.scrollEl
    if (!el) return

    // Touch: native momentum scroll via overflow-x: auto, just track overflow
    el.addEventListener('scroll', () => this.checkOverflow(), { passive: true })

    // Mouse: click-and-drag scroll for web
    el.addEventListener('mousedown', (e: MouseEvent) => {
      // Ignore if clicking a button
      if ((e.target as HTMLElement).closest('button')) return
      this.isHScrollDragging = true
      this.hasDragMoved = false
      this.dragStartX = e.clientX
      this.scrollStartX = el.scrollLeft
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
      e.preventDefault()
    })

    window.addEventListener('mousemove', (e: MouseEvent) => {
      if (!this.isHScrollDragging) return
      const dx = e.clientX - this.dragStartX
      if (Math.abs(dx) > 3) this.hasDragMoved = true
      el.scrollLeft = this.scrollStartX - dx
    })

    window.addEventListener('mouseup', () => {
      if (!this.isHScrollDragging) return
      this.isHScrollDragging = false
      el.style.cursor = ''
      el.style.userSelect = ''
      // Suppress the click that follows a drag
      if (this.hasDragMoved) {
        el.addEventListener('click', e => e.stopPropagation(), { capture: true, once: true })
      }
    })
  }

  // --- Expand/collapse drag handling ---

  private handleExpandPointerDown = (e: PointerEvent): void => {
    this.isExpandDragging = true
    this.expandDragStartY = e.clientY
    this.expandStartHeight = this.expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT
    this.toggleAttribute('dragging', true)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    e.preventDefault()
  }

  private handleExpandPointerMove = (e: PointerEvent): void => {
    if (!this.isExpandDragging) return
    const dy = e.clientY - this.expandDragStartY
    const newHeight = Math.max(
      COLLAPSED_HEIGHT,
      Math.min(EXPANDED_HEIGHT, this.expandStartHeight + dy)
    )
    this.style.setProperty('--bar-height', `${newHeight}px`)
  }

  private handleExpandPointerUp = (e: PointerEvent): void => {
    if (!this.isExpandDragging) return
    this.isExpandDragging = false
    this.toggleAttribute('dragging', false)

    const dy = e.clientY - this.expandDragStartY

    if (Math.abs(dy) < DRAG_THRESHOLD) {
      // Tap — toggle
      this.expanded = !this.expanded
    } else {
      // Drag — snap based on direction
      this.expanded = dy > 0
    }

    const targetHeight = this.expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT
    this.style.setProperty('--bar-height', `${targetHeight}px`)

    this.dispatchEvent(
      new CustomEvent('context-bar-expanded', {
        detail: { expanded: this.expanded },
        bubbles: true,
      })
    )
  }

  private cleanupExpandDrag(): void {
    this.isExpandDragging = false
    this.toggleAttribute('dragging', false)
  }

  private handleEvent(event: Event): void {
    this.dispatchEvent(
      new CustomEvent(event.type, {
        detail: (event as CustomEvent).detail,
        bubbles: true,
      })
    )
  }

  override render(): unknown {
    let content

    if (this.mapMode === 'map') {
      content = html`
        <timeline-bar
          .timelineState=${this.timelineState}
          @timeline-play=${this.handleEvent}
          @timeline-pause=${this.handleEvent}
          @timeline-reset=${this.handleEvent}
          @timeline-scrub=${this.handleEvent}
          @timeline-range-changed=${this.handleEvent}
          @timeline-speed-changed=${this.handleEvent}
          @timeline-loop-toggled=${this.handleEvent}
        ></timeline-bar>
      `
    } else if (this.mapMode === 'tracking') {
      content = html`
        <status-bar
          .currentLocation=${this.currentLocation}
          .currentElevation=${this.currentElevation}
          .currentHeading=${this.currentHeading}
          .currentVelocity=${this.currentVelocity}
          .unitSystem=${this.unitSystem}
          .isRecording=${this.isRecording}
          .locationPermissionGranted=${this.locationPermissionGranted}
          .currentActivity=${this.currentActivity}
          @toggle-recording=${this.handleEvent}
        ></status-bar>
      `
    } else if (this.mapMode === 'draw') {
      if (this.drawSubContext === 'tools') {
        content = html`
          <drawing-tools-bar
            .selectedTool=${this.selectedTool}
            @tool-selected=${this.handleEvent}
          ></drawing-tools-bar>
        `
      } else {
        content = html`
          <editing-tools-bar
            .selectedFeatures=${this.selectedFeatures}
            .splitEnabled=${this.splitEnabled}
            .activeStyleProperty=${this.activeStyleProperty}
            @feature-deleted=${this.handleEvent}
            @feature-extend=${this.handleEvent}
            @feature-split=${this.handleEvent}
            @feature-return=${this.handleEvent}
            @selection-cleared=${this.handleEvent}
            @open-style-editor=${this.handleEvent}
            @edit-saved=${this.handleEvent}
            @edit-cancelled=${this.handleEvent}
          ></editing-tools-bar>
        `
      }
    } else if (this.mapMode === 'measure') {
      content = html`
        <measure-tools-bar
          .unitSystem=${this.unitSystem}
          .distance=${this.measureDistance}
          .bearing=${this.measureBearing}
          .area=${this.measureArea}
          .perimeter=${this.measurePerimeter}
          .measureType=${this.measureType}
          .location=${this.measureLocation}
          @measure-clear=${this.handleEvent}
        ></measure-tools-bar>
      `
    } else {
      content = html`
        <selection-tools-bar
          .selectedCount=${this.selectedFeatures.length + this.overlayFeatureCount}
          .userFeatureCount=${this.selectedFeatures.length}
          .overlayFeatureCount=${this.overlayFeatureCount}
          .hasPlanContext=${this.hasPlanContext}
          .selectedFeatures=${this.selectedFeatures}
          .joinPreview=${this.joinPreview}
          @delete-selected=${this.handleEvent}
          @join-preview=${this.handleEvent}
          @join-confirmed=${this.handleEvent}
          @join-cancelled=${this.handleEvent}
          @selection-cleared=${this.handleEvent}
          @open-style-editor=${this.handleEvent}
          @add-to-plan=${this.handleEvent}
          @create-plan-from-selection=${this.handleEvent}
        ></selection-tools-bar>
      `
    }

    return html`
      <div class="collapsed-bar">
        <div class="overflow-left"></div>
        <div class="context-scroll">
          <div class="context-content">${content}</div>
        </div>
        <div class="overflow-right"></div>
      </div>
      ${this.expandable
        ? html`
            <div
              class="drag-handle"
              @pointerdown=${this.handleExpandPointerDown}
              @pointermove=${this.handleExpandPointerMove}
              @pointerup=${this.handleExpandPointerUp}
              @pointercancel=${this.handleExpandPointerUp}
            >
              <div class="drag-handle-pill"></div>
            </div>
            ${this.expanded
              ? html`
                  <div class="expanded-content">
                    <div class="tab-bar">
                      <button
                        class="tab-btn"
                        ?active=${this.activeTab === 'elevation'}
                        @click=${() => (this.activeTab = 'elevation')}
                      >
                        Elevation
                      </button>
                      <button
                        class="tab-btn"
                        ?active=${this.activeTab === 'distance'}
                        @click=${() => (this.activeTab = 'distance')}
                      >
                        Distance
                      </button>
                      <button
                        class="tab-btn"
                        ?active=${this.activeTab === 'aspect'}
                        @click=${() => (this.activeTab = 'aspect')}
                      >
                        Aspect
                      </button>
                    </div>
                    <div class="tab-content">
                      ${this.activeTab === 'elevation'
                        ? html`<elevation-profile
                            .routeProfile=${this.routeProfile}
                            .trackProfile=${this.trackProfile}
                            .currentDistance=${this.currentRouteDistance}
                            .waypoints=${this.routeWaypoints}
                            .unitSystem=${this.unitSystem}
                            .distanceTime=${this.distanceTime}
                          ></elevation-profile>`
                        : this.activeTab === 'distance'
                          ? html`<distance-time-chart
                              .points=${this.distanceTime}
                              .unitSystem=${this.unitSystem}
                            ></distance-time-chart>`
                          : html`<track-aspect-rose
                              .counts=${this.aspectCounts}
                            ></track-aspect-rose>`}
                    </div>
                  </div>
                `
              : nothing}
          `
        : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-context-bar': CairnContextBar
  }
}
